import { useMemo } from 'react';

import type { PaymentMethodType, PaymentRow } from './usePaymentRows';

interface UseDuplicatePaymentCheckParams {
    paymentMethodType: PaymentMethodType;
    paymentRows: PaymentRow[];
    isContractFullyScheduled: (contractId: string) => boolean;
    hasActiveScheduledPayment: (contractId: string) => boolean;
    t: (key: string) => string;
}

interface DuplicatePaymentCheckEntry {
    isDuplicate: boolean;
    infoMessage?: string;
}

/**
 * Checks if a payment has already been scheduled for contracts in the payment rows.
 * Blocks card payments if:
 * 1. All invoices for a contract are already covered by scheduled card payments, OR
 * 2. Any scheduled payment (any platform, pending or processed) exists for the contract
 * This prevents duplicate payments and mixed payment method scenarios.
 */
export const useDuplicatePaymentCheck = ({
    paymentMethodType,
    paymentRows,
    isContractFullyScheduled,
    hasActiveScheduledPayment,
    t,
}: UseDuplicatePaymentCheckParams) => {
    // Per-row duplicate payment check info
    const duplicatePaymentCheckByRow: DuplicatePaymentCheckEntry[] = useMemo(
        () =>
            paymentRows.map(row => {
                if (paymentMethodType !== 'card') {
                    return { isDuplicate: false };
                }

                const contractId = row.dbContractId || row.contractId;
                const isFullyScheduled = isContractFullyScheduled(contractId);
                const hasScheduled = hasActiveScheduledPayment(contractId);

                if (isFullyScheduled) {
                    return {
                        isDuplicate: true,
                        infoMessage: t('makePayment.cardPaymentAlreadyScheduled'),
                    };
                }

                if (hasScheduled) {
                    return {
                        isDuplicate: true,
                        infoMessage: t('makePayment.scheduledPaymentExists'),
                    };
                }

                return { isDuplicate: false };
            }),
        [paymentRows, paymentMethodType, isContractFullyScheduled, hasActiveScheduledPayment, t],
    );

    return {
        duplicatePaymentCheckByRow,
    };
};
